/**
 * Derives environment type from the current hostname (for use in browser).
 * Stg -> admin.stg.atlassian.com, prod -> admin.atlassian.com.
 */
export function getEnvTypeFromHostname(hostname: string): 'dev' | 'staging' | 'prod' {
	if (typeof hostname !== 'string') {
		return 'prod';
	}
	const h = hostname.toLowerCase();
	if (h.includes('dev.') || h.includes('.dev')) {
		return 'dev';
	}
	if (h.includes('stg') || h.includes('staging')) {
		return 'staging';
	}
	return 'prod';
}

/**
 * Base admin URL with no org path. Used when orgId is not available (message requires orgId to hydrate; fallback for CTA).
 * Stg: https://admin.stg.atlassian.com, Prod: https://admin.atlassian.com
 */
export function getAdminBaseUrl(envType: string): string {
	switch (envType) {
		case 'dev':
			return 'https://admin.dev.atlassian.com';
		case 'staging':
			return 'https://admin.stg.atlassian.com';
		case 'prod':
			return 'https://admin.atlassian.com';
		default:
			return 'https://admin.stg.atlassian.com';
	}
}

/**
 * Builds the org-level Rovo admin hub URL (same as Confluence Rovo onboarding CTA).
 * Used for Jira side-nav and Confluence side-nav so both link to the same Rovo setup experience.
 * Stg: https://admin.stg.atlassian.com/o/{orgId}/rovo, Prod: https://admin.atlassian.com/o/{orgId}/rovo
 */
export function getRovoOnboardingAdminUrl(orgId: string, envType: string): string {
	switch (envType) {
		case 'dev':
			return `https://admin.dev.atlassian.com/o/${orgId}/rovo`;
		case 'staging':
			return `https://admin.stg.atlassian.com/o/${orgId}/rovo`;
		case 'prod':
			return `https://admin.atlassian.com/o/${orgId}/rovo`;
		default:
			return `https://admin.stg.atlassian.com/o/${orgId}/rovo`;
	}
}

/**
 * URL for the "Get started" CTA. When orgId is present (required for message to hydrate), returns the org Rovo page; otherwise the plain admin base URL.
 */
export function getRovoOnboardingClickUrl(orgId: string | undefined, envType: string): string {
	if (orgId && orgId.trim() !== '') {
		return getRovoOnboardingAdminUrl(orgId, envType);
	}
	return getAdminBaseUrl(envType);
}
